import {
	ReactElement,
	useEffect,
	useState
} from "react";
import {
	isCancelledError
} from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import {
	Dialog,
	FormattedText,
	Form,
	FormItem,
	TextInput,
	ValidationError,
	Toolbar,
	SecretValueInput,
	convertibleTextInput,
	ConvertedTextInputProps
} from "client/Components";
import { useFormHandler, IFormHandler } from "client/Forms";
import {
	createValidationConfiguration,
	IValidationConfiguration,
	InputValidationResult,
	InputValidationResultKind
} from "client/Validation";
import {
	isNotNullNorUndefined,
	isNotEmpty,
	doesNotExceedLength,
	isUrl,
	isPositive,
	isConvertible,
	isInt32
} from "client/Validation/rules";
import {
	ZaaksysteemConnectionSaveModel,
	create,
	update,
	get,
	ZaaksysteemConnectionListItemModel
} from "client/Api/ZaaksysteemConfiguration/zaaksysteemConfiguration";
import { SecretValue } from "client/Api/Apps/common";
import {
	AlertManager,
	useAlerts,
	useUnmountedCancellation
} from "client/Hooks";
import { integerConverter } from "client/Forms/converters";
import { IValueInputAttachable } from "client/Forms/attachable";

const NumberInput: (props: ConvertedTextInputProps<number | null>) => ReactElement | null = convertibleTextInput<number | null>(integerConverter);

export type ZaaksysteemConnectionDialogProps = {
	connectionId?: number;
	onAfterSave(item: ZaaksysteemConnectionListItemModel): void;
	onClose(): void;
};
export const ZaaksysteemConnectionDialog = (props: ZaaksysteemConnectionDialogProps): ReactElement => {
	const {
		connectionId,
		onAfterSave,
		onClose
	} = props;

	const [isBusy, setIsBusy] = useState<boolean>(false);
	const [model, setModel] = useState<ZaaksysteemConnectionSaveModel>({
		name: "",
		serverUrl: "",
		apiInterfaceId: null,
		apiKey: {
			containsNewValue: true,
			value: ""
		}
	});
	const alertManager: AlertManager = useAlerts();
	const globalCancellation: AbortSignal = useUnmountedCancellation();

	const formHandler: IFormHandler<ZaaksysteemConnectionSaveModel> = useFormHandler<ZaaksysteemConnectionSaveModel>(
		model,
		setModel,
		validationConfiguration
	);

	useEffect(
		(): (() => void) | undefined => {
			if (connectionId === undefined) {
				return undefined;
			}

			const loadConnection = async (signal: AbortSignal): Promise<void> => {
				setIsBusy(true);

				try {
					const {
						name,
						serverUrl,
						apiInterfaceId
					} = await get(
						connectionId,
						signal
					);

					setModel({
						name,
						serverUrl,
						apiInterfaceId,
						apiKey: {
							containsNewValue: false,
							value: null
						}
					});

					setIsBusy(false);

				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}
					console.error(e);
					alertManager.showError(localize("_GeneralErrorMessage"));
					onClose();
				}
			};

			const cancellation = new AbortController();
			loadConnection(cancellation.signal);

			return () => cancellation.abort();
		},
		[
			alertManager,
			connectionId,
			formHandler.reducer,
			onClose
		]
	);

	const isNewConnection: boolean = connectionId === undefined;

	let titleKey: LocalizationKey;
	let saveKey: LocalizationKey;
	if (isNewConnection) {
		titleKey = "_NewNameConnection";
		saveKey = "_Add";
	} else {
		titleKey = "_EditNameConnection";
		saveKey = "_Save";
	}

	const submitHandler = async (): Promise<void> => {
		if (isBusy) {
			return;
		}

		setIsBusy(true);
		const validationResult: InputValidationResult = await formHandler.validation
			.validate();

		if (validationResult.kind === InputValidationResultKind.Invalid) {
			setIsBusy(false);
			return;
		}

		let id: number | undefined = connectionId;
		try {
			if (id === undefined) {
				id = (await create(model, globalCancellation))
					.connectionId;
			} else {
				await update(id, model, globalCancellation);
			}

			onClose();
			onAfterSave({
				id,
				name: model.name,
				serverUrl: model.serverUrl
			});
		} catch (e) {
			if (isCancelledError(e)) {
				return;
			}

			if (!formHandler.validation.tryProcessServerError(e)) {
				alertManager.showError(localize("_ChangesNotSaved"));
			}
			setIsBusy(false);
		}
	};

	const enforceToSupplyApiKey = (): void => {
		const attachable: IValueInputAttachable<SecretValue> = formHandler.for("apiKey");

		const { containsNewValue } = attachable.value;
		if (containsNewValue) {
			return;
		}

		attachable.reducer
			.setValue({
				containsNewValue: true,
				value: ""
			});

		attachable.validation
			.triggerInputFinished();
	};

	return (
		<Dialog hasBusyIndicator={isBusy} trackingId="zaaksysteem-connection-dialog">
			<Dialog.Header
				title={
					<FormattedText
						text={localize(titleKey)}
						thingsToReplace={[
							{ what: "{{Name}}", filler: localize("_Zaaksysteem")}
						]}
					/>
				}
				onClose={onClose}
			/>
			<Dialog.Body>
				<Form onSubmit={submitHandler}>
					<FormItem label={localize("_Name")}>
						<TextInput {...formHandler.for("name").attachToTextInput()} />
						<ValidationError {...formHandler.for("name").attachToValidationError()} />
					</FormItem>

					<FormItem label={localize("_ApiUrl")}>
						<TextInput
							{
								...formHandler.for("serverUrl")
									.attachToTextInput()
									.withOnChange(enforceToSupplyApiKey)
							}
						/>
						<ValidationError {...formHandler.for("serverUrl").attachToValidationError()} />
					</FormItem>

					<FormItem label={localize("_ApiInterfaceId")}>
						<NumberInput
							{...formHandler.for("apiInterfaceId")
								.attachToTextInput()
								.withOnChange(enforceToSupplyApiKey)
							}
						/>
						<ValidationError {...formHandler.for("apiInterfaceId").attachToValidationError()} />
					</FormItem>

					<FormItem label={localize("_ApiKey")}>
						<SecretValueInput
							{...formHandler.for("apiKey").attachToSecretValueInput()}
							containsInitialValue={!isNewConnection}
						/>
						<ValidationError
							{...formHandler.for("apiKey")
								.for("value")
								.attachToValidationError()
							}
						/>
					</FormItem>
				</Form>
			</Dialog.Body>
			<Dialog.Footer>
				<Toolbar>
					<Toolbar.Button
						kind="primary"
						text={localize(saveKey)}
						onClick={submitHandler}
						trackingId="save"
					/>

					<Toolbar.Button
						kind="secondary"
						text={localize("_Cancel")}
						onClick={onClose}
						trackingId="cancel"
					/>
				</Toolbar>
			</Dialog.Footer>
		</Dialog>
	);
};
ZaaksysteemConnectionDialog.displayName = "ZaaksysteemConnectionDialog";

const validationConfiguration: IValidationConfiguration<ZaaksysteemConnectionSaveModel> = createValidationConfiguration<ZaaksysteemConnectionSaveModel>();

validationConfiguration.forScalar("name")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty(),
		doesNotExceedLength(255)
	);

validationConfiguration.forScalar("serverUrl")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty(),
		isUrl({
			allowHttpsOnly: true,
			allowQueryString: false
		})
	);

validationConfiguration.forScalar("apiInterfaceId")
	.add(
		isConvertible(),
		isNotNullNorUndefined(),
		isPositive(),
		isInt32()
);

validationConfiguration.forObject("apiKey")
	.when((x) => x.containsNewValue)
	.forScalar("value")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty()
	);
